/**
 * The portal's light/dark choice.
 *
 * ── THREE STATES, NOT TWO ────────────────────────────────────────────
 *
 * `system` is not "light". It means *follow the OS*, and it is the default, so
 * a portal opened on a machine set to dark is dark without anyone choosing
 * anything. The other two are an explicit override that must win over the OS in
 * BOTH directions — including Light on a machine set to dark, which is the case
 * a bare `prefers-color-scheme` media query cannot express.
 *
 * ── THE CSS DOES THE WORK, NOT THIS FILE ─────────────────────────────
 *
 * `@ridgewill/brand`'s `tokens.css` ships the dark palette twice:
 *
 *     @media (prefers-color-scheme: dark) { :root:not([data-theme="light"]) { … } }
 *     :root[data-theme="dark"] { … }
 *
 * So `system` needs NO attribute at all — the media query handles it — and the
 * only job here is to stamp `data-theme` for an explicit choice. That is why
 * `apply` REMOVES the attribute for `system` rather than setting it to
 * something: an attribute value of "system" would match neither rule and would
 * silently pin the portal to light.
 *
 * ── tokens.css IS REQUIRED HERE, UNLIKE EVERYWHERE ELSE ──────────────
 *
 * Every `C.*` token is `var(--rw-x, <light>)`, so this portal rendered
 * correctly for months without ever importing the stylesheet. Dark cannot work
 * that way: a fallback holds one value and a theme needs two. `main.tsx`
 * imports it, and removing that import would not break the portal — it would
 * quietly delete dark mode and leave this control doing nothing.
 */

export type ThemeChoice = 'system' | 'light' | 'dark';

/** Also read by the inline script in index.html. Changing it needs both. */
export const THEME_KEY = 'rw-admin-theme';

export const THEME_CHOICES: readonly ThemeChoice[] = ['system', 'light', 'dark'];

function isChoice(v: unknown): v is ThemeChoice {
  return v === 'system' || v === 'light' || v === 'dark';
}

/**
 * What the admin last chose, or `system`.
 *
 * Every access is guarded. `localStorage` is not merely empty in a private
 * window or with site data blocked — reading it THROWS, and an exception here
 * would take the whole shell down over a colour preference.
 */
export function readTheme(): ThemeChoice {
  try {
    const raw = localStorage.getItem(THEME_KEY);
    return isChoice(raw) ? raw : 'system';
  } catch {
    return 'system';
  }
}

/** Stamp the root so the `[data-theme]` rules in tokens.css apply. */
export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);
}

/** Remember the choice and apply it. Storage failing must not stop the change. */
export function setTheme(choice: ThemeChoice): void {
  try {
    if (choice === 'system') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, choice);
  } catch {
    // A private window can still switch theme for this tab; it just will not
    // be remembered. Losing the preference is a smaller failure than refusing
    // to honour it.
  }
  applyTheme(choice);
}

/** What the viewer actually sees right now, which `system` alone does not say. */
export function resolvedTheme(choice: ThemeChoice): 'light' | 'dark' {
  if (choice !== 'system') return choice;
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}
